import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@entities/User';
import { useModuleCreatorStore } from '@features/CreateModule';
import { trackEvent } from '@shared/analytics';
import { buildComposerState, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, PlusIcon } from '@shared/ui';

/**
 * «Встретилось новое слово?» на главной (решение владельца 2026-10-23): где создать карточку и модуль — «Слова и модули».
 * «Создать карточку» — туда со строкой создания в фокусе; «Новый модуль» — модалка, после неё «Слова и модули» с этим
 * модулем (ModuleCreatorModal). Почта не подтверждена и бесплатная карточка потрачена (2026-11-02) — блока нет: строка
 * закрыта, а о почте просит плашка вверху главной (тестировщик: приглашение вело в закрытую строку). Высота — по содержимому; карточка от
 * 700 px (широкая колонка главной, слева под игрой) — в строку (DashboardPage.css, ТЗ перепланировки P1-1).
 */
export function CreateWordCard({ phone }: { phone: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openCreator = useModuleCreatorStore((s) => s.openCreator);
  const locked = useAuthStore((s) => !!s.user && !s.user.emailVerified && !s.user.guestCardAvailable);

  const toCard = () => {
    trackEvent('home_create_clicked', { action: 'card' });
    navigate(paths.words, { state: buildComposerState() });
  };
  const toModule = () => {
    trackEvent('home_create_clicked', { action: 'module' });
    openCreator();
  };

  if (locked) return null;

  return (
    <section
      aria-labelledby="dash-create-title"
      className={cn('dash-create rounded-card border border-solid border-line bg-card', phone ? 'p-4' : 'px-6 py-5')}
    >
      {/* Обёртка — для вида в строку: контейнер запроса — сама карточка, её раскладку он не меняет. */}
      <div className={cn('dash-create-body flex flex-col', phone ? 'gap-3' : 'gap-4')}>
        <div className="dash-create-head flex min-w-0 items-start gap-3">
          <span aria-hidden className="grid h-10 w-10 flex-none place-items-center rounded-full bg-accent/15 text-accent">
            <PlusIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 id="dash-create-title" className="font-serif text-[20px] font-medium leading-[26px] text-fg">
              {t('dashboard.home.create.title')}
            </h2>
            <p className="mt-1 text-[14px] leading-5 text-muted">{t('dashboard.home.create.text')}</p>
          </div>
        </div>
        <div className="dash-create-actions flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={toCard}>
            {t('dashboard.home.create.card')}
            <span aria-hidden>→</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={toModule}>
            {t('dashboard.home.create.module')}
          </Button>
        </div>
      </div>
    </section>
  );
}
